import type { MaybeRefOrGetter } from 'vue'
import type { BetterAuthClientPlugin } from 'better-auth/client'
import type { auth } from 'auth'
import { socialRedirectClient } from 'auth/src/social-redirect-client-plugin'
import { computed, ref, watch, toValue } from 'vue'
import { createAuthClient } from 'better-auth/vue'
import {
    inferAdditionalFields,
    anonymousClient,
} from 'better-auth/client/plugins'
import {
    getSessionCookie,
    setSessionCookie,
    getAnonymousUserIdCookie,
    setAnonymousUserIdCookie,
    clearAnonymousUserIdCookie,
    BACKEND_URL,
    AUTH_BASE_PATH,
} from '~/utils'

function anonymousReauthClient() {
    return {
        id: 'anonymous-reauth' as const,
        getActions: ($fetch) => ({
            signIn: {
                anonymousReauth: async (data: { userId: string }) => {
                    return $fetch<{
                        token: string
                        user: Record<string, unknown>
                    }>('/sign-in/anonymous-reauth', {
                        method: 'POST',
                        body: data,
                    })
                },
            },
        }),
        pathMethods: {
            '/sign-in/anonymous-reauth': 'POST' as const,
        },
    } satisfies BetterAuthClientPlugin
}

function externalAuthClient() {
    return {
        id: 'external-auth' as const,
        getActions: ($fetch) => ({
            signIn: {
                externalToken: async (data: { token: string }) => {
                    return $fetch<{
                        token: string
                        user: Record<string, unknown>
                    }>('/sign-in/external-token', {
                        method: 'POST',
                        body: data,
                    })
                },
            },
        }),
        pathMethods: {
            '/sign-in/external-token': 'POST' as const,
        },
        atomListeners: [
            {
                matcher: (path: string) => path === '/sign-in/external-token',
                signal: '$sessionSignal' as const,
            },
        ],
    } satisfies BetterAuthClientPlugin
}

type AuthClientOptions = {
    baseURL?: string
    basePath?: string
    plugins: [
        ReturnType<typeof inferAdditionalFields<typeof auth>>,
        ReturnType<typeof anonymousClient>,
        ReturnType<typeof anonymousReauthClient>,
        ReturnType<typeof externalAuthClient>,
        ReturnType<typeof socialRedirectClient>,
    ]
}

export type AuthClient = ReturnType<typeof createAuthClient<AuthClientOptions>>

export function useChatbotAuth(
    options: {
        baseUrl?: string
        basePath?: string
        externalToken?: MaybeRefOrGetter<string | undefined>
        awaitExternalToken?: boolean
        /**
         * Host-session mode: the widget never signs in on its own and sends
         * no Authorization header; the session comes from the host
         * platform's better-auth cookie (sent thanks to
         * `credentials: 'include'`). The session belongs to the host, so
         * account management is hidden and `signOut` is a no-op. Intended
         * for the widget embedded in the platform itself (Backoffice
         * copilot, spec platform-copilot-session-bridge.md); it only works
         * where the host cookie context works, not on third-party embeds.
         */
        useHostSession?: MaybeRefOrGetter<boolean | undefined>
        isActive?: MaybeRefOrGetter<boolean>
        cookieName?: string
        /** Called when a session is established or the user changes. */
        onSignedIn?: (detail: { userId: string; isAnonymous: boolean }) => void
        /** Called when the session ends without an immediate replacement. */
        onSignedOut?: () => void
        /**
         * Called when an external token sign-in fails (expired token, invalid
         * signature or disabled config): the host should provide a fresh token.
         */
        onExternalAuthExpired?: () => void
    } = {},
) {
    const _token = ref(getSessionCookie(options.cookieName))
    const useHostSession = computed(
        () => toValue(options.useHostSession) ?? false,
    )

    const token = computed({
        get: () => _token.value,
        set: (token: string) => {
            setSessionCookie(token, options.cookieName)
            _token.value = token
        },
    })

    const headers = computed(() =>
        !useHostSession.value && token.value
            ? { Authorization: `Bearer ${token.value}` }
            : undefined,
    )

    const client = createAuthClient({
        baseURL: options.baseUrl ?? BACKEND_URL,
        basePath: options.basePath ?? AUTH_BASE_PATH,
        plugins: [
            inferAdditionalFields<typeof auth>(),
            anonymousClient(),
            anonymousReauthClient(),
            externalAuthClient(),
            socialRedirectClient(),
        ],
        fetchOptions: {
            onSuccess: (ctx) => {
                if (useHostSession.value) {
                    // The host session token must never be persisted as the
                    // widget's own Bearer: it would keep impersonating the
                    // host after the mode is turned off.
                    return
                }
                const newTokenFromHeader =
                    ctx.response.headers.get('set-auth-token')
                if (newTokenFromHeader) {
                    token.value = newTokenFromHeader
                }
            },
            auth: {
                type: 'Bearer',
                // Host-session mode relies on the cookie alone: the widget's
                // stored Bearer (e.g. a previous anonymous session) would
                // shadow it.
                token: () => (useHostSession.value ? '' : token.value),
            },
            // Only host-session mode may exchange cookies: with credentials
            // on, an anonymous sign-in's Set-Cookie would REPLACE the user's
            // platform session cookie on the backend origin.
            customFetchImpl: (input, init) =>
                fetch(input, {
                    ...init,
                    credentials: useHostSession.value
                        ? 'include'
                        : init?.credentials,
                }),
        },
    }) as AuthClient

    const session = client.useSession()
    const isAuthenticating = ref(false)
    const externalToken = computed(() => toValue(options.externalToken))
    const isActive = computed(() => toValue(options.isActive) ?? true)

    /**
     * External token currently authorized for automatic (re-)authentication.
     * Follows the `externalToken` option and imperative sign-in calls; cleared
     * by `signOut`, so the watchers never re-login with a token the host has
     * withdrawn.
     */
    const activeExternalToken = ref(externalToken.value)

    // External token / host-session mode: the host application owns the auth
    // lifecycle, so the widget must not offer account management (sign-out,
    // profile).
    const isExternalAuth = computed(
        () =>
            !!externalToken.value ||
            !!options.awaitExternalToken ||
            !!activeExternalToken.value ||
            useHostSession.value,
    )

    /**
     * Serializes sign-in operations: a request arriving while another is in
     * flight runs right after it instead of being dropped. Each task re-checks
     * state when it starts, so superseded requests no-op.
     */
    let authChain: Promise<void> = Promise.resolve()
    function runExclusiveAuth(task: () => Promise<void>): Promise<void> {
        const run = authChain.then(task, task)
        authChain = run
        return run
    }

    // Notify the host on session transitions, whatever flow caused them
    // (anonymous, external token, email, social).
    watch(
        () => session.value.data?.user,
        (user, previousUser) => {
            if (user && user.id !== previousUser?.id) {
                options.onSignedIn?.({
                    userId: user.id,
                    isAnonymous: !!user.isAnonymous,
                })
            }
            if (!user && previousUser) {
                options.onSignedOut?.()
            }
        },
    )

    async function signInAnonymously(): Promise<void> {
        const { data } = await client.signIn.anonymous()
        if (data?.token) {
            token.value = data.token
            if (data.user?.id) {
                setAnonymousUserIdCookie(
                    data.user.id as string,
                    options.cookieName,
                )
            }
        }
    }

    /**
     * Attempts to create a new session for an existing anonymous user.
     * Returns true if re-auth succeeded, false otherwise.
     */
    async function tryAnonymousReauth(): Promise<boolean> {
        const storedUserId = getAnonymousUserIdCookie(options.cookieName)
        if (!storedUserId) {
            return false
        }
        try {
            const { data } = await client.signIn.anonymousReauth({
                userId: storedUserId,
            })
            if (data?.token) {
                token.value = data.token
                await session.value.refetch()
                return true
            }
        } catch {
            // user record may have been deleted — fall back to creating a new one
        }
        return false
    }

    /** Re-authenticates the stored anonymous user, or creates a fresh one. */
    async function reauthOrSignInAnonymously(): Promise<void> {
        const reauthed = await tryAnonymousReauth()
        if (!reauthed) {
            await signInAnonymously()
        }
    }

    /**
     * The external token was rejected (expired, invalid or config disabled):
     * notify the host so it can provide a fresh one, then apply the fallback
     * (keep waiting in `awaitExternalToken` mode, anonymous otherwise).
     */
    async function handleExternalTokenFailure(
        failedExternalToken: string,
    ): Promise<void> {
        if (activeExternalToken.value !== failedExternalToken) {
            // Withdrawn (sign-out) or replaced while the request was in flight
            return
        }
        options.onExternalAuthExpired?.()
        if (options.awaitExternalToken) {
            return
        }
        await reauthOrSignInAnonymously()
    }

    /** Raw token exchange; must run inside `runExclusiveAuth`. */
    async function exchangeExternalToken(
        newExternalToken: string,
    ): Promise<void> {
        isAuthenticating.value = true
        try {
            const { data } = await client.signIn.externalToken({
                token: newExternalToken,
            })
            if (activeExternalToken.value !== newExternalToken) {
                // Withdrawn (sign-out) or replaced while the request was in flight
                return
            }
            if (data?.token) {
                token.value = data.token
                await session.value.refetch()
                return
            }
            await handleExternalTokenFailure(newExternalToken)
        } catch {
            await handleExternalTokenFailure(newExternalToken)
        } finally {
            isAuthenticating.value = false
        }
    }

    /** External token currently queued or in flight, to drop duplicate requests. */
    let pendingExternalToken: string | undefined

    /**
     * Last external token an exchange was attempted with, whatever the
     * trigger. The session watcher consults it so a token that was already
     * tried is never auto-retried: a rejected token must not loop between
     * exchange and fallback. Deliberate retries (prop change, imperative
     * `signIn`, 401 handler) bypass it.
     */
    let attemptedExternalToken: string | undefined

    /** The cookie-restored external session was reconciled with the host state. */
    let externalSessionReconciled = false

    async function signInWithExternalToken(
        newExternalToken: string,
    ): Promise<void> {
        if (!newExternalToken || pendingExternalToken === newExternalToken) {
            return
        }
        activeExternalToken.value = newExternalToken
        attemptedExternalToken = newExternalToken
        pendingExternalToken = newExternalToken
        try {
            await runExclusiveAuth(async () => {
                if (activeExternalToken.value !== newExternalToken) {
                    // Superseded while waiting for the previous operation
                    return
                }
                await exchangeExternalToken(newExternalToken)
            })
        } finally {
            pendingExternalToken = undefined
        }
    }

    async function signInAnonymouslyWithFallback(): Promise<void> {
        if (isAuthenticating.value) {
            // The `[isActive, session]` watcher retries on the next session change
            return
        }
        await runExclusiveAuth(async () => {
            if (activeExternalToken.value || useHostSession.value) {
                // A host-owned sign-in was requested in the meantime
                return
            }
            isAuthenticating.value = true
            try {
                await reauthOrSignInAnonymously()
            } finally {
                isAuthenticating.value = false
            }
        })
    }

    watch(
        [isActive, session],
        ([newIsActive, newSession]) => {
            if (!newIsActive) {
                return
            }
            if (
                !newSession.isPending &&
                !newSession.isRefetching &&
                !isAuthenticating.value &&
                !newSession.data
            ) {
                if (useHostSession.value) {
                    // Host-session mode: the session comes from the host
                    // cookie, the widget never signs in on its own. No
                    // session means the host is not (or no longer) logged in.
                    return
                }
                if (activeExternalToken.value) {
                    signInWithExternalToken(activeExternalToken.value)
                    return
                }
                if (options.awaitExternalToken) {
                    return
                }
                signInAnonymouslyWithFallback()
                return
            }

            const user = newSession.data?.user

            if (useHostSession.value) {
                // Host-session mode: the session is read from (and owned by)
                // the host cookie, none of the widget-session housekeeping
                // below applies.
                return
            }

            // A session created by an external token exchange (the only kind
            // with `hardExpiresAt`) may have been restored from the cookie
            // after a page navigation, where prop changes cannot be observed:
            // reconcile it once with the auth state the host declares now.
            if (
                newSession.data?.session?.hardExpiresAt &&
                !externalSessionReconciled
            ) {
                externalSessionReconciled = true
                if (!activeExternalToken.value) {
                    // The host no longer provides a token (it logged out
                    // before this page load): drop the stale session. The
                    // usual fallback rules apply (keep waiting in
                    // `awaitExternalToken` mode, anonymous otherwise).
                    signOut()
                    return
                }
                if (attemptedExternalToken !== activeExternalToken.value) {
                    // Re-exchange the current token, so a token minted for a
                    // different user replaces the restored session and an
                    // expired one raises `external-auth-expired` right away.
                    signInWithExternalToken(activeExternalToken.value)
                    return
                }
            }

            // Upgrade an anonymous session restored from the cookie when an
            // external token is available (e.g. the user logged into the host
            // app between page loads). Attempted once per token.
            if (
                user?.isAnonymous &&
                activeExternalToken.value &&
                attemptedExternalToken !== activeExternalToken.value
            ) {
                signInWithExternalToken(activeExternalToken.value)
                return
            }

            // Backfill userId cookie for existing anonymous sessions
            if (
                user?.isAnonymous &&
                user.id &&
                !getAnonymousUserIdCookie(options.cookieName)
            ) {
                setAnonymousUserIdCookie(user.id as string, options.cookieName)
            }
        },
        { immediate: true },
    )

    // Host-session mode toggle: on entry re-read the session from the host
    // cookie (the widget Bearer is already suppressed by the mode); on exit
    // re-read without the cookie, so the `[isActive, session]` watcher
    // re-establishes the widget's own flow (anonymous or external token).
    watch(useHostSession, () => {
        session.value.refetch()
    })

    watch(externalToken, (newToken, previousToken) => {
        if (newToken) {
            // Track the intent even when inactive: the `[isActive, session]`
            // watcher signs in with it once the widget becomes active.
            activeExternalToken.value = newToken
            if (isActive.value) {
                signInWithExternalToken(newToken)
            }
            return
        }
        // The host withdrew the token (logged out): sign out without
        // re-authenticating; the fallback rules apply (keep waiting in
        // `awaitExternalToken` mode, anonymous otherwise).
        if (previousToken && activeExternalToken.value === previousToken) {
            signOut()
        }
    })

    function onError(error: { type: string; title: string; status: number }) {
        if (error.status !== 401) {
            return
        }
        if (useHostSession.value) {
            // The widget cannot recover a host session on its own (a 401
            // here means the host session expired): re-read it so the UI
            // reflects the signed-out state.
            session.value.refetch()
            return
        }
        if (activeExternalToken.value) {
            signInWithExternalToken(activeExternalToken.value)
            return
        }
        if (options.awaitExternalToken) {
            return
        }
        signInAnonymouslyWithFallback()
    }

    const userId = computed(() => session.value.data?.user?.id)

    // Auth methods for widget login screen
    async function signInWithEmail(
        email: string,
        password: string,
        rememberMe?: boolean,
    ) {
        const result = await client.signIn.email({
            email,
            password,
            rememberMe,
        })
        // Capture the session token from the response body. Relying solely on
        // the `set-auth-token` header is unreliable here, so we mirror the
        // anonymous/social flows and store the token explicitly.
        if (result.data?.token) {
            token.value = result.data.token
            // Signing in from an anonymous session links (and deletes) the
            // anonymous user server-side, so its stored userId can no longer
            // be re-authenticated — drop it so a later sign-out falls straight
            // back to a fresh anonymous session.
            clearAnonymousUserIdCookie(options.cookieName)
        }
        return result
    }

    function signUpWithEmail(params: {
        email: string
        password: string
        name: string
    }) {
        return client.signUp.email(params)
    }

    function requestPasswordReset(email: string, redirectTo: string) {
        return client.requestPasswordReset({ email, redirectTo })
    }

    /**
     * Opens a popup for social OAuth sign-in.
     *
     * The popup performs a top-level navigation to the backend `social-redirect`
     * endpoint, so the OAuth `state` cookie is set first-party on the backend
     * origin and survives the round-trip (a cross-origin XHR `signIn.social`
     * would set it as a third-party cookie that browsers drop, causing
     * `state_mismatch`). After the flow completes, the backend callback page
     * posts back the session Bearer token, which we store before refetching.
     */
    async function signInWithSocialPopup(
        provider: 'google' | 'microsoft',
        onAuthSuccess?: () => void,
    ): Promise<{ popupBlocked: boolean }> {
        const baseUrl = options.baseUrl ?? BACKEND_URL
        const basePath = options.basePath ?? AUTH_BASE_PATH
        const callbackURL = `${baseUrl}/${basePath}/widget-oauth-callback`
        const redirectUrl = client.signIn.socialRedirect({
            provider,
            callbackURL,
        })

        const popup = window.open(
            redirectUrl,
            'eightwave-oauth',
            'width=600,height=700,menubar=no,toolbar=no',
        )

        if (!popup) {
            return { popupBlocked: true }
        }

        return new Promise((resolve) => {
            const allowedOrigin = new URL(baseUrl).origin
            // Resolve too if the user closes the popup without completing OAuth.
            const closedPollId = setInterval(() => {
                if (popup?.closed) {
                    finish()
                }
            }, 500)

            function finish() {
                window.removeEventListener('message', onOAuthMessage)
                clearInterval(closedPollId)
                resolve({ popupBlocked: false })
            }

            function onOAuthMessage(event: MessageEvent) {
                const isTrustedOAuthMessage =
                    event.origin === allowedOrigin &&
                    event.data?.type === 'eightwave-oauth'
                if (!isTrustedOAuthMessage) {
                    return
                }
                popup?.close()
                if (event.data.token) {
                    token.value = event.data.token
                    // Same as the email flow: the anonymous user was linked
                    // and deleted server-side, its stored userId is stale.
                    clearAnonymousUserIdCookie(options.cookieName)
                }
                session.value.refetch().then(() => onAuthSuccess?.())
                finish()
            }

            window.addEventListener('message', onOAuthMessage)
        })
    }

    /**
     * Signs the current user out and clears the locally stored session token.
     * Withdraws the active external token first, so the watchers never
     * re-authenticate with it: in external mode the host signs the widget back
     * in (prop change or `signInWithExternalToken`), while in the other modes
     * the `[isActive, session]` watcher re-establishes an anonymous session
     * (re-using the stored anonymous userId cookie when present).
     */
    async function signOut(): Promise<void> {
        if (useHostSession.value) {
            // The session belongs to the host application (cookie): revoking
            // it from the widget would log the user out of the host too.
            // Leaving host-session mode is the host's move (prop change).
            return
        }
        activeExternalToken.value = undefined
        const revokedToken = token.value
        try {
            await client.signOut()
        } finally {
            // A concurrent exchange may have stored a fresh session token
            // while the revoke request was in flight (e.g. a host `signIn`
            // racing the mount-time reconcile): only clear our own token,
            // never the newer one.
            if (token.value === revokedToken) {
                token.value = ''
            }
            await session.value.refetch()
        }
    }

    return {
        client,
        session,
        isAuthenticating,
        isExternalAuth,
        token,
        headers,
        onError,
        userId,
        signInWithEmail,
        signUpWithEmail,
        requestPasswordReset,
        signInSocial: client.signIn.social,
        signInWithSocialPopup,
        signInWithExternalToken,
        signOut,
    }
}
